import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "../../Const/Url";
import { logout_func2 } from "../../Const/logoutCommon";
import { showToast } from "../../../utils/ToastHelper";
import Loader from "../../CustomCommons/Loader";
import DeleteIcon from "../../CustomCommons/DeleteIcon";
import EditIcon from "../../CustomCommons/EditIcon";
import plus from "../../../assets/Icon/plus.svg";

const LIMIT = 10;

const CategoryBannerList = () => {
  const history = useHistory();
  const [banners, setBanners] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [offset, setOffset] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  const getOffsetFromUrl = (url) => {
    if (!url) return 0;
    try {
      const parsed = new URL(url);
      return Number(parsed.searchParams.get("offset") || 0);
    } catch (error) {
      const [, queryString] = url?.split("?") || [];
      const params = new URLSearchParams(queryString);
      return Number(params.get("offset") || 0);
    }
  };

  const fetchBanners = useCallback(
    (searchText = "", { urlOverride, offsetValue = 0 } = {}) => {
      setIsLoading(true);
      const resolvedOffset =
        typeof offsetValue === "number" && offsetValue >= 0 ? offsetValue : 0;

      let requestUrl = urlOverride;
      if (!requestUrl) {
        const params = new URLSearchParams();
        params.append("limit", LIMIT);
        params.append("offset", resolvedOffset);
        if (searchText) {
          params.append("search", searchText);
        }
        requestUrl = `${BASE_URL}api/v1/frontend/admin/v2/custom-categories/?${params.toString()}`;
      }

      axios
        .get(requestUrl)
        .then((res) => {
          const payload = res?.data?.data || {};
          setBanners(payload?.results || []);
          setTotalData(payload?.count || 0);
          setNextUrl(payload?.next || null);
          setPrevUrl(payload?.previous || null);
          setOffset(resolvedOffset);
          setPageNumber(Math.floor(resolvedOffset / LIMIT) + 1);
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message ||
            err?.response?.data?.errors?.non_field_errors?.[0];
          const status = err?.response?.status;

          if (message === "Invalid token." || status === 401) {
            logout_func2(() => {}, history);
            return;
          }
          showToast("error", message || "Failed to load category banners");
        })
        .finally(() => setIsLoading(false));
    },
    [history]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchBanners(query, { offsetValue: 0 });
    }, 400);

    return () => clearTimeout(debounce);
  }, [query, fetchBanners]);

  const totalPages = useMemo(() => {
    if (!LIMIT) return 1;
    return Math.ceil((totalData || 0) / LIMIT) || 1;
  }, [totalData]);

  const handlePaginationPrevious = () => {
    if (!prevUrl) return;
    const derivedOffset = getOffsetFromUrl(prevUrl);
    fetchBanners(query, { urlOverride: prevUrl, offsetValue: derivedOffset });
  };

  const handlePaginationNext = () => {
    if (!nextUrl) return;
    const derivedOffset = getOffsetFromUrl(nextUrl);
    fetchBanners(query, { urlOverride: nextUrl, offsetValue: derivedOffset });
  };

  const handleCreate = () => {
    history.push("/website-settings/create-category-banner");
  };

  const handleEdit = (item) => {
    const identifier = item?.slug || item?.id;
    if (!identifier) {
      showToast("error", "Unable to identify category banner");
      return;
    }
    history.push(`/website-settings/edit-category-banner/${identifier}`);
  };

  const handleDelete = (item) => {
    const identifier = item?.slug;
    if (!identifier) {
      showToast("error", "Unable to identify category banner");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this data.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) {
        swal("Your data is safe!");
        return;
      }

      axios
        .delete(
          `${BASE_URL}api/v1/frontend/admin/v2/custom-categories/${identifier}/`
        )
        .then(() => {
          swal("Category banner deleted!", { icon: "success" });
          showToast("success", "Category banner deleted successfully");
          fetchBanners(query, { offsetValue: offset });
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message ||
            err?.response?.data?.errors?.non_field_errors?.[0];
          showToast("error", message || "Failed to delete category banner");
        });
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderBoolean = (value, truthy = "Yes", falsy = "No") => {
    if (typeof value !== "boolean") return value ?? "-";
    return value ? (
      <span className="badge bg-success">{truthy}</span>
    ) : (
      <span className="badge bg-secondary">{falsy}</span>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent">
      <div className="cotagorey-head mb-4">
        <div>
          <h3>Category Banners</h3>
          <input
            style={{ color: "black" }}
            type="text"
            className="filter-input"
            placeholder="Search here"
            onChange={(e) => setQuery(e.target?.value)}
          />
        </div>
        <div className="filter_div">
          <div className="button-div" onClick={handleCreate}>
            <img src={plus} alt="Add category banner" />
            <span>Add Category Banner</span>
          </div>
        </div>
      </div>

      <div style={{ display: "block", overflowX: "auto" }}>
        <table className="table_my pdf-table my-3">
          <thead
            className="bg-dark text-white head-row mt-3 main-head-print"
            style={{ height: "50px" }}
          >
            <tr>
              <th style={{ width: "100px", paddingLeft: "10px"  }}>Media</th>
              <th style={{ minWidth: "220px", paddingLeft: "10px" }}>
                Banner Title
              </th>
              <th style={{ minWidth: "120px" }}>Order</th>
              <th style={{ minWidth: "170px" }}>Start At</th>
              <th style={{ minWidth: "170px" }}>End At</th>
              <th style={{ minWidth: "160px" }}>Currently Active</th>
              <th style={{ minWidth: "130px" }}>Default</th>
              <th style={{ minWidth: "130px" }}>Status</th>
              <th style={{ minWidth: "170px" }}>Created At</th>
              <th style={{ minWidth: "170px" }}>Updated At</th>
              <th style={{ width: "140px", textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody className="tb">
            {banners?.length ? (
              banners.map((item, index) => (
                <tr key={item?.id || index} className="table-row">
                  <td style={{  paddingLeft: "10px"  }}>
                    {item?.media_type === 0 ? (
                      <img
                        style={{
                          width: "50px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                        src={
                          item?.banner_details?.resized_doc_url ||
                          item?.banner_details?.doc_url
                        }
                        alt="Category Banner"
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "4px",
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M8 5V19L19 12L8 5Z" fill="#666" />
                        </svg>
                      </div>
                    )}
                  </td>
                  <td style={{ paddingLeft: "10px" }}>
                    {item?.banner_title || "-"}
                  </td>
                  <td>{item?.order ?? "-"}</td>
                  <td>{formatDate(item?.start_at)}</td>
                  <td>{formatDate(item?.end_at)}</td>
                  <td>{renderBoolean(item?.is_currently_active)}</td>
                  <td>{renderBoolean(item?.is_default)}</td>
                  <td>
                    {renderBoolean(item?.is_active, "Active", "Inactive")}
                  </td>
                  <td>{formatDate(item?.created_at)}</td>
                  <td>{formatDate(item?.updated_at)}</td>
                  <td className="action">
                    <div className="d-flex justify-content-center gap-3">
                      <div
                        className="edit-del green"
                        onClick={() => handleEdit(item)}
                      >
                        <EditIcon />
                      </div>
                      <div
                        className="edit-del red"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11">
                  <div className="not_found d-flex justify-content-center align-items-center py-4">
                    <h4 className="my-4">No Data Found</h4>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalData > 0 && (
        <div className="row pag" style={{ marginTop: "30px" }}>
          <div className="col-12 col-md-4">
            <p className="inter" style={{ color: "#AEAEB2", fontSize: "16px" }}>
              Total Entries:{" "}
              <span style={{ color: "black", fontSize: "14px" }}>
                {totalData}
              </span>
            </p>
          </div>
          {totalPages > 1 && (
            <div className="col-12 col-md-8 d-flex justify-content-md-end">
              <div className="pagination-controls d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationPrevious}
                  disabled={!prevUrl}
                >
                  Prev
                </button>
                <span className="d-flex align-items-center">
                  Page {pageNumber} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationNext}
                  disabled={!nextUrl}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryBannerList;
